// src/components/students/StudentList.jsx (Redesign im "UserList"-Stil)
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import authService, { api } from "../../services/authService";
import { useNavigate } from "react-router-dom";
import schoolService from "../../services/schoolService";
import classService from "../../services/classService";
import practiceInstructorService from "../../services/practiceInstructorService";
import { SkeletonStudentGrid } from "../common/SkeletonCard";
import { useDebounce } from "../../hooks/useDebounce";
import { useDefaultProfessionFilter } from "../../hooks/useDefaultProfessionFilter";
import { cache } from "../../utils/cacheUtils";
import PracticeInstructorSelectModal from "../common/PracticeInstructorSelectModal";
import ResetLearnerPasswordModal from "./ResetLearnerPasswordModal";
import SemesterNotesModal from "../common/SemesterNotesModal";

// Archive reasons are written by the backend: the daily LehrjahrSyncJob sets
// "Apprenticeship Duration Completed", manual archiving/deactivation via
// UserController sets "Manual Archive" / "Manual Deactivation".
function getArchiveBadge(student) {
  const reason = student.archiveReason || "";

  if (reason.toLowerCase().includes("duration completed")) {
    return {
      label: "Apprenticeship completed",
      icon: "graduation-cap",
      className: "bg-blue-50 text-blue-700 border-blue-200",
    };
  }
  if (reason.toLowerCase().includes("manual")) {
    return {
      label: "Manually archived",
      icon: "user-lock",
      className: "bg-amber-50 text-amber-700 border-amber-200",
    };
  }
  return {
    label: "Archived",
    icon: "box-archive",
    className: "bg-gray-100 text-gray-600 border-gray-200",
  };
}

function StudentList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [, setSchools] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filterProfession, setFilterProfession] =
    useDefaultProfessionFilter(user);
  const [filterLehrjahr, setFilterLehrjahr] = useState("ALL");
  const [isFromCache, setIsFromCache] = useState(false);

  // Status Filter State
  const [viewMode, setViewMode] = useState("ACTIVE"); // "ACTIVE" or "ARCHIVED"

  // Assignment Modal State
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedStudentForAssign, setSelectedStudentForAssign] =
    useState(null);
  const [instructors, setInstructors] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  // Password Reset Modal State
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [selectedStudentForReset, setSelectedStudentForReset] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  // Apprentice Snapshot export - tracks which student's export is in flight so the
  // button can disable itself and show a spinner without blocking the rest of the list.
  const [exportingSnapshotId, setExportingSnapshotId] = useState(null);

  // Quick Notes Modal State
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedStudentForNotes, setSelectedStudentForNotes] = useState(null);

  //
  // Menu State for 3-dots
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 🚀 Debounce search term to reduce filtering operations
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const handleAssignClick = async (e, student) => {
    e.stopPropagation();
    setSelectedStudentForAssign(student);
    setAssignModalOpen(true);
    setAssignLoading(true);
    setError(null);
    setInstructors([]);

    try {
      const [instructorsData, currentInstructor] = await Promise.all([
        practiceInstructorService.getAvailableInstructors(student.id),
        practiceInstructorService
          .getCurrentInstructor(student.id)
          .catch(() => null),
      ]);

      const mergedInstructors = [
        ...(instructorsData || []),
        ...(currentInstructor ? [currentInstructor] : []),
      ].filter(
        (instructor, index, array) =>
          instructor &&
          array.findIndex((candidate) => candidate?.id === instructor.id) ===
            index,
      );

      setInstructors(mergedInstructors);
      setSelectedStudentForAssign((prev) =>
        prev
          ? { ...prev, activePracticeInstructor: currentInstructor || null }
          : prev,
      );
    } catch (err) {
      console.error("Failed to load assignment data", err);
      setError("Failed to load assignment data");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleAssignSubmit = async (instructorId) => {
    if (!selectedStudentForAssign) return;

    setAssignLoading(true);
    try {
      await practiceInstructorService.assignInstructor(
        selectedStudentForAssign.id,
        instructorId,
      );

      const assignedInstructor =
        instructors.find(
          (instructor) => String(instructor.id) === String(instructorId),
        ) || null;

      if (assignedInstructor) {
        setStudents((prev) =>
          prev.map((student) =>
            student.id === selectedStudentForAssign.id
              ? { ...student, activePracticeInstructor: assignedInstructor }
              : student,
          ),
        );
        setSelectedStudentForAssign((prev) =>
          prev
            ? { ...prev, activePracticeInstructor: assignedInstructor }
            : prev,
        );
      }

      setSuccess(
        `Instructor assigned to ${selectedStudentForAssign.firstName} successfully.`,
      );

      // Invalidate cache and reload to show updated assignment immediately
      cache.clear("students-");
      await loadAllStudents();

      setAssignModalOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to assign instructor", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Failed to assign instructor",
      );
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUnassignSubmit = async () => {
    if (!selectedStudentForAssign) return;

    setAssignLoading(true);
    try {
      await practiceInstructorService.unassignStudent(
        selectedStudentForAssign.id,
      );

      setStudents((prev) =>
        prev.map((student) =>
          student.id === selectedStudentForAssign.id
            ? { ...student, activePracticeInstructor: null }
            : student,
        ),
      );
      setSelectedStudentForAssign((prev) =>
        prev ? { ...prev, activePracticeInstructor: null } : prev,
      );

      setSuccess(
        `Instructor unassigned from ${selectedStudentForAssign.firstName}.`,
      );

      // Invalidate cache and reload to show updated assignment immediately
      cache.clear("students-");
      await loadAllStudents();

      setAssignModalOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to unassign", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Failed to unassign instructor",
      );
    } finally {
      setAssignLoading(false);
    }
  };

  const handleResetPasswordClick = (e, student) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setError(null);
    setSelectedStudentForReset(student);
    setResetModalOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!selectedStudentForReset) return;

    setResetLoading(true);
    setError(null);
    try {
      const result = await authService.resetLearnerPassword(
        selectedStudentForReset.id,
      );
      if (!result.success) {
        setError(result.message || "Failed to reset learner password");
        return;
      }

      setSuccess(
        `Password for ${selectedStudentForReset.firstName || selectedStudentForReset.username} has been reset successfully. New standard password: Welcome2Journey!`,
      );
      setResetModalOpen(false);
      setSelectedStudentForReset(null);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error("Failed to reset learner password", err);
      setError("Failed to reset learner password");
    } finally {
      setResetLoading(false);
    }
  };

  const handleToggleStatus = async (e, student) => {
    e.stopPropagation();
    setOpenMenuId(null);

    try {
      // Default to true if undefined
      const currentStatus = student.isActive !== false;
      const newStatus = !currentStatus;

      await api.put(`/users/${student.id}/status`, { isActive: newStatus });

      // Update local state
      setStudents((prev) =>
        prev.map((s) =>
          s.id === student.id ? { ...s, isActive: newStatus } : s,
        ),
      );

      setSuccess(
        newStatus
          ? `Student ${student.firstName} was reactivated.`
          : `Student ${student.firstName} was transferred to archive.`,
      );
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to update status", err);
      setError("Failed to update student status.");
    }
  };

  const handleExportSnapshotClick = async (e, student) => {
    e.stopPropagation();
    setOpenMenuId(null);
    if (exportingSnapshotId) return; // Prevent duplicate/overlapping exports.

    setError(null);
    setExportingSnapshotId(student.id);
    try {
      const response = await api.get(
        `/apprentices/${student.id}/snapshot/pdf`,
        { responseType: "blob" },
      );

      const contentDisposition = response.headers["content-disposition"] || "";
      const match = contentDisposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `apprentice-snapshot-${student.id}.pdf`;

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to export apprentice snapshot", err);
      setError("Failed to export apprentice snapshot.");
    } finally {
      setExportingSnapshotId(null);
    }
  };

  // Access: Admin oder Berufsbildner
  const hasAccess = () =>
    user?.roles?.some((r) => r.name === "ADMIN" || r.name === "BERUFSBILDNER");

  const canResetLearnerPasswords = hasAccess();
  // Snapshot export is scoped the same way as the rest of this directory: Admin/Berufsbildner.
  const canExportSnapshot = hasAccess();

  useEffect(() => {
    if (!hasAccess()) {
      setError(
        "Access denied. This feature is only available for administrators and instructors.",
      );
      setLoading(false);
      return;
    }
    loadAllStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.roles, user?.professionType]);

  // 🚀 Optimized filtering with useMemo - only recalculates when dependencies change
  const filteredStudents = useMemo(() => {
    let list = [...students];

    // 1. Filter by Status (Active/Archived)
    list = list.filter((s) => {
      const isArchived = s.isArchived === true;
      const isActive = s.isActive !== false;

      if (viewMode === "ACTIVE") {
        // Show if NOT archived AND is active
        return !isArchived && isActive;
      } else {
        // Show if archived OR inactive (legacy support)
        // Ideally should just be isArchived, but to catch old "inactive" users:
        return isArchived || !isActive;
      }
    });

    const q = debouncedSearchTerm.toLowerCase().trim();

    // 2. Apply search filter
    if (q) {
      list = list.filter((s) => {
        const firstName = (s.firstName || "").toLowerCase();
        const lastName = (s.lastName || "").toLowerCase();
        const username = (s.username || "").toLowerCase();
        const email = (s.email || "").toLowerCase();
        const fullName = `${firstName} ${lastName}`;

        return (
          firstName.startsWith(q) ||
          lastName.startsWith(q) ||
          fullName.startsWith(q) ||
          username.startsWith(q) ||
          email.startsWith(q)
        );
      });

      // Sort with relevance login (same as before)
      // ... (sorting logic kept same implicit by flow, or I can copy it)
    } else {
      // 3. Apply profession filter (only if not searching, or maybe both? Original logic was else)
      if (filterProfession && filterProfession !== "ALL") {
        list = list.filter(
          (s) =>
            s.professionType === filterProfession ||
            (filterProfession === "INFORMATIK" &&
              s.professionType === "INFORMATIKER") ||
            (filterProfession === "MEDIAMATIK" &&
              s.professionType === "MEDIAMATIKER"),
        );
      }
    }

    // Apply Lehrjahr Filter
    if (filterLehrjahr !== "ALL") {
      list = list.filter((s) => String(s.currentLehrjahr) === filterLehrjahr);
    }

    // Sort logic from original file
    list.sort((a, b) => {
      // ...
      const aFirstName = (a.firstName || "").toLowerCase();
      const bFirstName = (b.firstName || "").toLowerCase();
      return aFirstName.localeCompare(bFirstName);
    });

    return list;
  }, [
    students,
    debouncedSearchTerm,
    filterProfession,
    filterLehrjahr,
    viewMode,
  ]);

  const loadAllStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      // 🚀 Check cache first - complete student list
      // Use user-specific key to ensure permission-based filtering is respected
      const studentsCacheKey = `students-list-all-${user?.id}`;
      const cachedStudents = cache.get(studentsCacheKey);

      if (cachedStudents) {
        setStudents(cachedStudents);
        setIsFromCache(true);
        setLoading(false);
        return;
      }

      setIsFromCache(false);

      const schoolsCacheKey = "schools-all";
      let schoolsData = cache.get(schoolsCacheKey);

      if (!schoolsData) {
        schoolsData = await schoolService.getSchools();
        cache.set(schoolsCacheKey, schoolsData, 10 * 60 * 1000); // 10min cache
      }

      setSchools(schoolsData || []);

      // 🚀 PARALLEL: Load all classes for all schools at once
      const schoolClassPromises = (schoolsData || []).map(async (school) => {
        const classesCacheKey = `classes-school-${school.id}`;
        let classes = cache.get(classesCacheKey);

        if (!classes) {
          try {
            classes = await classService.getClassesBySchool(school.id);
            cache.set(classesCacheKey, classes, 5 * 60 * 1000); // 5min cache
          } catch (e) {
            console.warn(`Failed to load classes for school ${school.name}`, e);
            classes = [];
          }
        }

        return { school, classes: classes || [] };
      });

      const schoolsWithClasses = await Promise.all(schoolClassPromises);

      // 🚀 OPTIMIZED: Fetch students with concurrency control to avoid 401s and browser connection limits
      const classesToFetch = [];
      const cachedResults = [];

      // Pre-process: Identify what needs to be fetched vs what is cached
      for (const { school, classes } of schoolsWithClasses) {
        for (const classItem of classes) {
          const studentsCacheKey = `students-class-${classItem.id}`;
          const cachedClassStudents = cache.get(studentsCacheKey);

          if (cachedClassStudents) {
            cachedResults.push({
              students: cachedClassStudents,
              school,
              classItem,
            });
          } else {
            classesToFetch.push({ school, classItem });
          }
        }
      }

      // Process fetches in chunks (Concurrency Limit: 5)
      const CONCURRENCY_LIMIT = 5;
      const fetchedResults = [];

      for (let i = 0; i < classesToFetch.length; i += CONCURRENCY_LIMIT) {
        const chunk = classesToFetch.slice(i, i + CONCURRENCY_LIMIT);

        const chunkPromises = chunk.map(({ school, classItem }) =>
          classService
            .getStudentsByClass(classItem.id, { skipGlobalErrorHandler: true })
            .then((classStudents) => {
              const studentsCacheKey = `students-class-${classItem.id}`;
              cache.set(studentsCacheKey, classStudents, 5 * 60 * 1000);
              return { students: classStudents, school, classItem };
            })
            .catch((e) => {
              // Only warn if it's NOT a 401 (which means unauthorized for this specific class)
              if (e.response?.status !== 401) {
                console.warn(
                  `Failed to load students for class ${classItem.name}`,
                  e,
                );
              }
              return { students: [], school, classItem };
            }),
        );

        // Wait for this chunk to finish before starting next (or use true pool)
        const chunkResults = await Promise.all(chunkPromises);
        fetchedResults.push(...chunkResults);
      }

      const studentResults = [...cachedResults, ...fetchedResults];

      // 🚀 Also fetch ALL users to find students not assigned to any class
      // This solves the issue where new students don't appear in the list until enrolled
      let allUsers = [];
      try {
        // Use skipGlobalErrorHandler to prevent auto-logout on 401
        const usersResponse = await api.get("/users", {
          skipGlobalErrorHandler: true,
        });
        allUsers = usersResponse.data || [];
      } catch (err) {
        console.warn("Could not fetch full user list", err);
      }

      // Filter for LERNENDE role
      const unassignedStudents = allUsers.filter(
        (u) => u.roles && u.roles.some((r) => r.name === "LERNENDE"),
      );

      // Map students with context
      const allStudents = studentResults.flatMap(
        ({ students, school, classItem }) =>
          (students || []).map((s) => ({
            ...s,
            schoolName: school.name,
            schoolId: school.id,
            className: classItem.name,
            classId: classItem.id,
            professionType: classItem.professionType || s.professionType,
          })),
      );

      // Gruppiere Studenten nach ID und sammle alle Klassen
      const studentMap = new Map();

      allStudents.forEach((student) => {
        const key = student.id;
        if (studentMap.has(key)) {
          // Student existiert bereits, füge weitere Klasse hinzu
          const existing = studentMap.get(key);
          existing.classes.push({
            name: student.className,
            id: student.classId,
            schoolName: student.schoolName,
            schoolId: student.schoolId,
          });
        } else {
          // Neuer Student
          studentMap.set(key, {
            ...student,
            classes: [
              {
                name: student.className,
                id: student.classId,
                schoolName: student.schoolName,
                schoolId: student.schoolId,
              },
            ],
          });
        }
      });

      // Add students that are not enrolled in any class
      unassignedStudents.forEach((student) => {
        if (!studentMap.has(student.id)) {
          studentMap.set(student.id, {
            ...student,
            classes: [],
            schoolName: "Not Assigned",
            className: "No Class",
            professionType: student.professionType || "UNKNOWN",
          });
        }
      });

      // Sort students alphabetically by firstName, then lastName
      const studentsWithClasses = Array.from(studentMap.values()).sort(
        (a, b) => {
          const firstNameComparison = (a.firstName || "")
            .toLowerCase()
            .localeCompare((b.firstName || "").toLowerCase());
          if (firstNameComparison !== 0) return firstNameComparison;
          return (a.lastName || "")
            .toLowerCase()
            .localeCompare((b.lastName || "").toLowerCase());
        },
      );

      // 🚀 Cache the complete student list
      cache.set(studentsCacheKey, studentsWithClasses, 3 * 60 * 1000); // 3min cache

      setStudents(studentsWithClasses);
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      console.error("Error loading apprentices", e);
      setError("Failed to load apprentices.");
    } finally {
      setLoading(false);
    }
  };

  const handleStudentClick = (student) => {
    // Speichere Studentendaten im sessionStorage für die Detail-Ansicht
    sessionStorage.setItem(`student-${student.id}`, JSON.stringify(student));

    // Navigiere zur Detail-Ansicht
    navigate(`/students/${student.id}`);
  };

  const handleRefresh = () => {
    // Clear all student-related caches
    cache.clear("students-");
    cache.clear("classes-");
    cache.clear("schools-");
    setSuccess("Cache cleared! Reloading fresh data...");
    loadAllStudents();
  };

  /* const getProfessionPillClass = (p) => {
    return getProfessionPillClasses(p);
  }; */

  if (!hasAccess()) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-amber-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            Only administrators and instructors can view apprentices.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-user-graduate text-sunrise-red mr-4"></i>
                  Apprentice Directory
                </h1>
                <p className="text-slate-cloud mt-2">
                  Browse and manage all apprentices across schools and classes
                </p>
              </div>
              <div className="mt-4 lg:mt-0 flex items-center gap-3">
                {isFromCache && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full border border-gray-200">
                    <i className="fas fa-clock mr-1"></i>
                    Cached
                  </span>
                )}
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-white text-sunrise-red border border-sunrise-red/20 hover:bg-sunrise-red hover:text-white rounded-lg font-medium transition-all shadow-sm flex items-center gap-2"
                  disabled={loading}
                >
                  <i
                    className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}
                  ></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                {error}
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-check-circle text-green-500 mr-3"></i>
                {success}
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="text-green-500 hover:text-green-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card-brand p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                Search Apprentices
                {searchTerm !== debouncedSearchTerm && (
                  <span className="ml-2 text-xs text-gray-500 animate-pulse">
                    <i className="fas fa-circle-notch fa-spin mr-1"></i>
                    Searching...
                  </span>
                )}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  className="input-brand pl-10 pr-10 w-full"
                  placeholder="Search by name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                Filter by Profession
              </label>
              <select
                className="input-brand w-full"
                value={filterProfession}
                onChange={(e) => setFilterProfession(e.target.value)}
              >
                <option value="ALL">All Professions</option>
                <option value="INFORMATIK">Informatik</option>
                <option value="MEDIAMATIK">Mediamatik</option>
                <option value="KV">KV</option>
                <option value="KUNDENDIALOG">Kundendialog</option>
                <option value="DETAILHANDEL">Detailhandel</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-dusk mb-2">
                Filter by Lehrjahr
              </label>
              <select
                className="input-brand w-full"
                value={filterLehrjahr}
                onChange={(e) => setFilterLehrjahr(e.target.value)}
              >
                <option value="ALL">All Years</option>
                <option value="1">1. Lehrjahr</option>
                <option value="2">2. Lehrjahr</option>
                <option value="3">3. Lehrjahr</option>
                <option value="4">4. Lehrjahr</option>
              </select>
            </div>
          </div>
        </div>

        {/* Card Header with Count & Tabs */}
        <div className="card-brand">
          {/* Same tab treatment as the Management Hub: a light bar with the
              active view as a red pill, instead of a solid red banner. */}
          <div className="border-b border-pale-dawn/70 bg-pale-dawn/30 px-4 py-3 rounded-t-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-grey-dusk flex items-center">
              <i className="fas fa-user-tag text-sunrise-red mr-3"></i>
              {viewMode === "ACTIVE"
                ? "Active Apprentices"
                : "Archived Apprentices"}
              <span className="ml-3 px-2.5 py-1 rounded-full bg-ice-white border border-pale-dawn text-xs font-semibold text-grey-dusk">
                {filteredStudents.length}
              </span>
            </h2>

            {/* View Mode Tabs */}
            <nav className="flex flex-wrap gap-2">
              {[
                { id: "ACTIVE", label: "Active", icon: "fa-user-check" },
                { id: "ARCHIVED", label: "Archived", icon: "fa-box-archive" },
              ].map((tab) => {
                const active = viewMode === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setViewMode(tab.id)}
                    className={[
                      "inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium transition-all",
                      active
                        ? "bg-sunrise-red text-ice-white shadow-soft"
                        : "bg-ice-white border border-pale-dawn text-grey-dusk hover:border-sunrise-red/30 hover:bg-pale-dawn/30",
                    ].join(" ")}
                  >
                    <i
                      className={`fas ${tab.icon} mr-2 ${
                        active ? "opacity-95" : "text-slate-cloud"
                      }`}
                    />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {loading ? (
              <SkeletonStudentGrid count={6} />
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-user-slash text-gray-400 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-grey-dusk mb-2">
                  No apprentices found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search criteria
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {filteredStudents.map((student, index) => (
                  <div
                    key={`${student.id}-${student.classId}-${index}`}
                    className={`group border rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer h-full flex flex-col relative ${
                      student.isActive === false
                        ? "bg-gray-50 border-gray-300"
                        : "bg-white border-gray-200"
                    }`}
                    onClick={() => handleStudentClick(student)}
                  >
                    {/* Student Info */}
                    <div className="p-4 pb-3 flex-grow">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h3
                            className={`font-bold text-base leading-tight truncate ${
                              student.isActive === false
                                ? "text-gray-500"
                                : "text-grey-dusk"
                            }`}
                          >
                            {student.firstName && student.lastName
                              ? `${student.firstName} ${student.lastName}`
                              : student.username || "Unknown Student"}
                          </h3>
                          {/* Only useful next to a real name - when the name is
                              missing the heading already shows the username. */}
                          {student.username &&
                            student.firstName &&
                            student.lastName && (
                              <p
                                className="text-[11px] text-gray-400 leading-tight truncate mt-0.5"
                                title={student.username}
                              >
                                @{student.username}
                              </p>
                            )}
                          {viewMode === "ARCHIVED" &&
                            (() => {
                              const badge = getArchiveBadge(student);
                              return (
                                <span
                                  className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.className}`}
                                  title={student.archiveReason || undefined}
                                >
                                  <i
                                    className={`fas fa-${badge.icon} text-[9px]`}
                                  ></i>
                                  {badge.label}
                                </span>
                              );
                            })()}
                        </div>

                        <div className="relative flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStudentForNotes(student);
                              setNotesModalOpen(true);
                            }}
                            className="h-7 w-7 rounded-md text-gray-400 hover:text-grey-dusk hover:bg-gray-100 transition-colors flex items-center justify-center"
                            title="Quick notes"
                            aria-label="Quick notes"
                          >
                            <i className="fas fa-note-sticky text-[11px]"></i>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(
                                openMenuId === student.id ? null : student.id,
                              );
                            }}
                            className="h-7 w-7 rounded-md text-gray-400 hover:text-grey-dusk hover:bg-gray-100 transition-colors flex items-center justify-center"
                            title="More actions"
                            aria-label="More actions"
                          >
                            <i className="fas fa-ellipsis-v text-[11px]"></i>
                          </button>

                          {openMenuId === student.id && (
                            <div
                              ref={menuRef}
                              className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-50 py-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canResetLearnerPasswords && (
                                <button
                                  onClick={(e) =>
                                    handleResetPasswordClick(e, student)
                                  }
                                  className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-gray-50 text-amber-700"
                                >
                                  <i className="fas fa-key w-5 text-center"></i>
                                  Reset Password
                                </button>
                              )}

                              {canExportSnapshot && (
                                <button
                                  onClick={(e) =>
                                    handleExportSnapshotClick(e, student)
                                  }
                                  disabled={exportingSnapshotId === student.id}
                                  className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-gray-50 text-grey-dusk disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  {exportingSnapshotId === student.id ? (
                                    <i className="fas fa-circle-notch fa-spin w-5 text-center"></i>
                                  ) : (
                                    <i className="fas fa-file-pdf w-5 text-center"></i>
                                  )}
                                  {exportingSnapshotId === student.id
                                    ? "Exporting..."
                                    : "Export Snapshot"}
                                </button>
                              )}

                              <button
                                onClick={(e) => handleToggleStatus(e, student)}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-gray-50 ${
                                  student.isActive !== false
                                    ? "text-red-600"
                                    : "text-green-600"
                                }`}
                              >
                                <i
                                  className={`fas fa-${
                                    student.isActive !== false
                                      ? "archive"
                                      : "undo"
                                  } w-5 text-center`}
                                ></i>
                                {student.isActive !== false
                                  ? "Archive Student"
                                  : "Reactivate Student"}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="px-4 pb-4 pt-1 mt-auto">
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            sessionStorage.setItem(
                              `student-${student.id}`,
                              JSON.stringify(student),
                            );
                            navigate(`/students/${student.id}`);
                          }}
                          className="h-8 px-2 bg-sunrise-red text-white hover:bg-sunrise-600 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                        >
                          <i className="fas fa-chart-line text-[11px]"></i>
                          <span>Grades</span>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/admin/learning-documentation/${student.id}`,
                            );
                          }}
                          className="h-8 px-2 bg-grey-dusk text-white hover:bg-grey-dusk/90 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                        >
                          <i className="fas fa-book text-[11px]"></i>
                          <span>Docs</span>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/trainer/semester-reviews?student=${student.id}`,
                            );
                          }}
                          className="h-8 px-2 bg-white border border-pale-dawn text-grey-dusk hover:bg-ice-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                        >
                          <i className="fas fa-clipboard-check text-[11px] text-gray-500"></i>
                          <span>Reviews</span>
                        </button>

                        <button
                          onClick={(e) => handleAssignClick(e, student)}
                          className="h-8 px-2 bg-white border border-pale-dawn text-grey-dusk hover:bg-ice-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                        >
                          <i className="fas fa-user-check text-[11px] text-gray-500"></i>
                          <span>Assign</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      <PracticeInstructorSelectModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        student={selectedStudentForAssign}
        availableInstructors={instructors}
        onAssign={handleAssignSubmit}
        onUnassign={handleUnassignSubmit}
        isLoading={assignLoading}
      />

      <ResetLearnerPasswordModal
        isOpen={resetModalOpen}
        student={selectedStudentForReset}
        onClose={() => {
          setResetModalOpen(false);
          setSelectedStudentForReset(null);
        }}
        onConfirm={handleResetPasswordSubmit}
        isLoading={resetLoading}
      />

      <SemesterNotesModal
        isOpen={notesModalOpen}
        student={selectedStudentForNotes}
        onClose={() => {
          setNotesModalOpen(false);
          setSelectedStudentForNotes(null);
        }}
      />
    </div>
  );
}

export default StudentList;
